<template>
  <div>
    <multiform-controller
      :product-name="productName"
      :step-id="stepId"
      :questions="questions"
      :forms-data="questionData.convictions || []"
      :initial-data="initialData"
      :max-amount="4"
      form-label="conviction"
      product-title="conviction"
      :show-confirm-modal="showConfirmModal"
      :is-continue-triggered="isContinueTriggered"
      @set-data="questionData.convictions = $event"
      @multiform-questions-details="$emit('multiform-questions-details', $event)"
      @edit-process="$emit('edit-process', $event)"
      @reset-continue-trigger="$emit('reset-continue-trigger', $event)"
    >
      <template
        #preview="{ data: { data } }"
      >
        <subform-preview
          :preview-list="formatPreviewItem(data)"
          :icon="vehicleIcon"
        />
      </template>
    </multiform-controller>
  </div>
</template>

<script>
import pluralize from 'pluralize';
import QuestionBase from './Base';
import SubformPreview from '@/components/SubformController/SubformPreview';
import MultiformController from '@/components/MultiformController/MultiformController';
import { mapActions } from 'vuex';

export default {
  name: 'question-convicted-driver-convictions',
  extends: QuestionBase,
  components: {
    MultiformController,
    SubformPreview,
  },
  props: {
    productTitle: {
      type: String,
      required: true,
    },
    isContinueTriggered: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      initialData: {},
      questions: [
        {
          questionId: 'date-of-conviction',
          questionComponent: 'question-base-date',
          params: {
            customFields: {
              field: 'dateOfConviction',
            },
            title: 'What date did the conviction happen?',
            helpText: 'If unsure, conviction details can be found by viewing your driving licence information on the government website or you can also check via phone or post.',
            validationRules: {
              field: {
                dateOfIncident: {
                  itemType: 'conviction',
                },
                checkFutureIncident: {
                  itemType: 'conviction',
                },
              },
            },
          },
        },
        {
          questionId: 'conviction-type',
          questionComponent: 'question-base-search-other',
          params: {
            customFields: {
              search: 'convictionType',
            },
            title: 'What was the conviction for?',
            searchType: 'getConvictionOffenceCodes',
            topGroup: 'most_common_offence_codes',
            isImmediate: true,
            optionsConfig: {
              placeholder: 'Please select conviction code',
            },
            validationErrors: {
              search: {
                required: 'Please select conviction code.',
              },
            },
          },
        },
        {
          questionId: 'rehabilitation-course-taken',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'rehabilitationCourseTaken',
            },
            showRules: {
              convictionType: {
                regex: '^DR',
                required: true,
              },
            },
            title: 'Did you take a rehabilitation course?',
            defaultValues: {
              field: false,
            },
          },
        },
        {
          questionId: 'penalty-points',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'penaltyPoints',
            },
            title: 'Did you receive penalty points?',
            defaultValues: {
              field: false,
            },
          },
        },
        {
          questionId: 'points-received',
          questionComponent: 'question-base-dropdown',
          params: {
            title: 'How many penalty points did you get for this offence?',
            customFields: {
              field: 'pointsReceived',
            },
            showRules: {
              penaltyPoints: {
                isAny: {
                  other: [true],
                },
                required: true,
              },
            },
            optionsConfig: {
              placeholder: 'Please select no. of points',
              options: {
                1: '1 Point',
                2: '2 Points',
                3: '3 Points',
                4: '4 Points',
                5: '5 Points',
                6: '6 Points',
                7: '7 Points',
                8: '8 Points',
                9: '9 Points',
                10: '10 Points',
                11: '11 Points or more',
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select the number of penalty points.',
              },
            },
          },
        },
        {
          questionId: 'fined-for-conviction',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'finedForConviction',
            },
            title: 'Were you fined?',
            defaultValues: {
              field: false,
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'fine-ammount',
          params: {
            customFields: {
              field: 'fineAmmount',
            },
            showRules: {
              finedForConviction: {
                isAny: {
                  other: [true],
                },
                required: true,
              },
            },
            title: 'How much were you fined?',
            inputPrefix: '£',
            placeholder: 'e.g. 100',
            maxLength: 5,
            validationRules: {
              field: {
                required: true,
                between: [
                  0,
                  99999,
                ],
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter how much were you fined.',
              },
            },
          },
        },
        {
          questionId: 'driving-ban',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'drivingBan',
            },
            title: 'Were you banned?',
            defaultValues: {
              field: false,
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'length-of-ban',
          params: {
            customFields: {
              field: 'lengthOfBan',
            },
            showRules: {
              drivingBan: {
                isAny: {
                  other: [true],
                },
                required: true,
              },
            },
            title: 'How many months was this for?',
            placeholder: 'e.g. 6',
            maxLength: 3,
            withoutSeparators: true,
            validationRules: {
              field: {
                required: true,
                between: [
                  0,
                  999,
                ],
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter how many month you were banned for.',
              },
            },
          },
        },
      ],
    };
  },
  computed: {
    vehicleIcon () {
      switch (this.stepData.vehicleType) {
        case 'C':
          return 'mdi-car';
        case 'M':
          return 'mdi-motorbike';
        case 'V':
          return 'mdi-van-utility';
        default:
          return 'mdi-car';
      }
    },
  },
  methods: {
    ...mapActions(['showConfirmModal']),
    formatPreviewItem ({ dateOfConviction, convictionType, rehabilitationCourseTaken, pointsReceived, fineAmmount, lengthOfBan, drivingBan }) {
      const hasRehabilitionCourseTaken = rehabilitationCourseTaken !== null && rehabilitationCourseTaken !== '';

      return [
        {
          prepend: 'Reason for conviction:',
          value: convictionType,
        },
        {
          prepend: 'Happened on:',
          value: dateOfConviction,
        },
        hasRehabilitionCourseTaken && {
          prepend: 'Rehabilitation course:',
          value: rehabilitationCourseTaken ? 'Yes' : 'No',
        },
        fineAmmount && {
          prepend: 'Fined:',
          value: fineAmmount,
          model: `£ ${this.helpers.getNumberFormatted(fineAmmount)}`,
        },
        pointsReceived && {
          prepend: `<u>${pointsReceived} ${pluralize('point', pointsReceived)}</u> received`,
        },
        drivingBan && {
          prepend: `Received <u>${lengthOfBan} ${pluralize('month', lengthOfBan)}</u> ban period`,
        },
      ];
    },
  },
};
</script>
